import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Heart, Search, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { followService } from '../services/followService';
import { useNavigate } from 'react-router-dom';

// ==========================================
// RESPONSIVE STYLED COMPONENTS
// ==========================================
const SupporterContainer = styled.div`
  min-height: 100vh; background: var(--background-dark); padding: 40px 20px;
  @media (max-width: 768px) { padding: 20px 16px; }
`;

const SupporterHeader = styled.div`
  max-width: 1200px; margin: 0 auto; margin-bottom: 40px;
  @media (max-width: 768px) { margin-bottom: 24px; text-align: center; }
`;

const SupporterTitle = styled.h1`
  color: var(--text-white); font-size: 2.5rem; font-weight: 700; font-family: var(--font-heading); margin-bottom: 16px;
  @media (max-width: 768px) { font-size: 1.8rem; margin-bottom: 12px; }
`;

const SupporterSubtitle = styled.p`
  color: var(--text-gray); font-size: 1.125rem; font-family: var(--font-body);
  @media (max-width: 768px) { font-size: 1rem; }
`;

const StatsGrid = styled.div`
  display: grid; 
  /* Smanjen minmax da bi kartice mogle lepo da se prelome na telefonima */
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); 
  gap: 24px; max-width: 1200px; margin: 0 auto 40px;
  @media (max-width: 768px) { gap: 16px; margin-bottom: 24px; }
`;

const StatCard = styled(motion.div)`
  background: var(--secondary-black); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; text-align: center; 
  .stat-number { color: var(--primary-gold); font-size: 2.5rem; font-weight: 700; font-family: var(--font-heading); margin-bottom: 8px; @media (max-width: 768px) { font-size: 2rem; } } 
  .stat-label { color: var(--text-gray); font-size: 1rem; font-family: var(--font-body); @media (max-width: 768px) { font-size: 0.9rem; } }
`;

const SupporterGrid = styled.div`
  display: grid; 
  /* Smanjeno sa 350px na 280px da ne bi probijalo širinu ekrana na mobilnim uređajima */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr)); 
  gap: 24px; max-width: 1200px; margin: 0 auto;
  @media (max-width: 768px) { gap: 16px; }
`;

const SupporterCard = styled(motion.div)`
  background: var(--secondary-black); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.3s ease; 
  &:hover { border-color: var(--primary-gold); transform: translateY(-4px); }
  @media (max-width: 768px) { padding: 20px; }
`;

const CardHeader = styled.div`
  display: flex; align-items: center; gap: 16px; margin-bottom: 16px; 
  svg { color: var(--primary-gold); width: 24px; height: 24px; } 
  h3 { color: var(--text-white); font-size: 1.25rem; font-weight: 600; font-family: var(--font-heading); margin: 0; }
`;

const CardContent = styled.div`color: var(--text-gray); font-family: var(--font-body); line-height: 1.5;`;
const FollowingList = styled.div`max-width:1200px;margin:32px auto 0;background:#111;border:1px solid #333;border-radius:16px;padding:24px; h2{margin-bottom:18px;} @media(max-width:600px){padding:18px;}`;
const FollowingItem = styled.button`width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px;background:#080808;color:#fff;border:1px solid #292929;border-radius:10px;margin-top:10px;cursor:pointer;text-align:left;&:hover{border-color:#e0c16f}.handle{color:#888;font-size:.9rem;}`;

const SupporterDashboard = () => {
  const { user } = useAuth();
  const [followingCount, setFollowingCount] = useState(0);
  const [following, setFollowing] = useState([]);
  const navigate = useNavigate();

  // REAL API FETCH
  useEffect(() => {
    if (user) {
      loadRealStats();
    }
  }, [user]);

  const loadRealStats = async () => {
    try {
      const response = await followService.getFollowingList();
      // Izbroj koliko ljudi pratis u bazi
      setFollowingCount(response.following ? response.following.length : 0);
      setFollowing(response.following || []);
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const stats = [
    { number: followingCount, label: "Creators Supported" },
    { number: "$0", label: "Total Donated" } // Povezaćemo na plaćanja kasnije
  ];

  const supporterFeatures = [
    { icon: <Heart />, title: "My Subscriptions", description: "Manage your active memberships." },
    { icon: <Search />, title: "Discover Creators", description: "Find new creators to support." },
    { icon: <History />, title: "Donation History", description: "Track all your donations and receipts." }
  ];

  return (
    <SupporterContainer>
      <SupporterHeader>
        <SupporterTitle>Welcome back, {user?.username}!</SupporterTitle>
        <SupporterSubtitle>Manage your subscriptions, track your support, and discover amazing creators</SupporterSubtitle>
      </SupporterHeader>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
            <div className="stat-number">{stat.number}</div>
            <div className="stat-label">{stat.label}</div>
          </StatCard>
        ))}
      </StatsGrid>

      <SupporterGrid>
        {supporterFeatures.map((feature, index) => (
          <SupporterCard key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
            <CardHeader>{feature.icon}<h3>{feature.title}</h3></CardHeader>
            <CardContent>{feature.description}</CardContent>
          </SupporterCard>
        ))}
      </SupporterGrid>
      <FollowingList>
        <h2>Creators you follow</h2>
        {following.length ? following.map(creator => (
          <FollowingItem key={creator.creator_id} onClick={() => navigate(`/${creator.username}`)}>
            <span>{creator.display_name || creator.username}<span className="handle"> · @{creator.username}</span></span>
            <span style={{color:'#e0c16f'}}>View page →</span>
          </FollowingItem>
        )) : <p style={{color:'#888'}}>You are not following any creators yet.</p>}
      </FollowingList>
    </SupporterContainer>
  );
};

export default SupporterDashboard;
